import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api';
import './OnboardWizard.css';

// Import step components
import ConfigureStep from './mpx-tenant/ConfigureStep';
import TestConnectionStep from './mpx-tenant/TestConnectionStep';
import DetailsStep from './mpx-tenant/DetailsStep';
import ConfirmStep from './mpx-tenant/ConfirmStep';
import CompleteStep from './mpx-tenant/CompleteStep';

// Define wizard steps
type WizardStep = 'enter-did' | 'test-connection' | 'details' | 'confirm' | 'complete';

interface WizardState {
  currentStep: WizardStep;
  name: string | null;
  description: string | null;
  did: string | null;
  apiEndpoint: string | null;
  tenant: any;
}

const AddMpxTenantWizardPage: React.FC = () => {
  const navigate = useNavigate();

  const [wizardState, setWizardState] = useState<WizardState>({
    currentStep: 'enter-did',
    name: null,
    description: null,
    did: null,
    apiEndpoint: null,
    tenant: null,
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const steps: { key: WizardStep; label: string }[] = [
    { key: 'enter-did', label: 'Enter DID' },
    { key: 'test-connection', label: 'Test Connection' },
    { key: 'details', label: 'Details' },
    { key: 'confirm', label: 'Confirm' },
    { key: 'complete', label: 'Complete' },
  ];

  const currentStepIndex = steps.findIndex(s => s.key === wizardState.currentStep);

  const goToStep = (step: WizardStep) => {
    setWizardState(prev => ({ ...prev, currentStep: step }));
  };

  const nextStep = () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < steps.length) {
      goToStep(steps[nextIndex].key);
    }
  };

  const prevStep = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      goToStep(steps[prevIndex].key);
    }
  };

  const handleConfigured = (did: string) => {
    setWizardState(prev => ({
      ...prev,
      did,
    }));
    nextStep();
  };

  const handleConnectionTested = (apiEndpoint: string) => {
    setWizardState(prev => ({
      ...prev,
      apiEndpoint,
    }));
    nextStep();
  };

  const handleDetailsEntered = (data: { name: string; description: string }) => {
    setWizardState(prev => ({
      ...prev,
      name: data.name,
      description: data.description,
    }));
    nextStep();
  };

  const handleConfirm = async () => {
    try {
      setSaving(true);
      setError('');

      const response = await apiClient.post('/mpx-tenants', {
        name: wizardState.name,
        description: wizardState.description,
        did: wizardState.did,
      });

      setWizardState(prev => ({ ...prev, tenant: response.data }));
      nextStep();
    } catch (err: any) {
      console.error('Failed to create MPX tenant:', err);
      setError(err.message || 'Failed to create MPX tenant. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleFinish = () => {
    navigate('/integrations');
  };

  const handleViewTenant = () => {
    if (wizardState.tenant?.id) {
      navigate(`/mpx-tenants/edit/${wizardState.tenant.id}`);
    }
  };

  const handleCancel = () => {
    navigate('/integrations');
  };

  // Render current step
  const renderStep = () => {
    if (saving) {
      return (
        <div className="card shadow">
          <div className="card-body text-center py-5">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden"></span>
            </div>
            <p className="text-muted">Creating MPX tenant...</p>
          </div>
        </div>
      );
    }

    switch (wizardState.currentStep) {
      case 'enter-did':
        return (
          <ConfigureStep
            onNext={handleConfigured}
            onCancel={handleCancel}
          />
        );
      case 'test-connection':
        return (
          <TestConnectionStep
            did={wizardState.did!}
            onSuccess={handleConnectionTested}
            onBack={prevStep}
            onCancel={handleCancel}
          />
        );
      case 'details':
        return (
          <DetailsStep
            onNext={handleDetailsEntered}
            onBack={prevStep}
            onCancel={handleCancel}
          />
        );
      case 'confirm':
        return (
          <ConfirmStep
            name={wizardState.name!}
            description={wizardState.description!}
            did={wizardState.did!}
            apiEndpoint={wizardState.apiEndpoint!}
            onConfirm={handleConfirm}
            onBack={prevStep}
            onCancel={handleCancel}
          />
        );
      case 'complete':
        return (
          <CompleteStep
            tenant={wizardState.tenant}
            onFinish={handleFinish}
            onViewTenant={handleViewTenant}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <h1 className="h3 mb-0 text-gray-800">
          <i className="fas fa-cloud mr-2"></i>
          Add MPX Tenant Wizard
        </h1>
        <button
          className="btn btn-sm btn-secondary"
          onClick={handleCancel}
        >
          <i className="fas fa-times mr-1"></i>
          Cancel
        </button>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="close" onClick={() => setError('')} aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      )}

      {/* Progress Bar */}
      <div className="card shadow mb-4">
        <div className="card-body">
          <div className="wizard-progress">
            <div className="wizard-steps">
              {steps.map((step, index) => (
                <div
                  key={step.key}
                  className={`wizard-step ${
                    index === currentStepIndex
                      ? 'active'
                      : index < currentStepIndex
                      ? 'completed'
                      : ''
                  }`}
                >
                  <div className="wizard-step-circle">
                    {index < currentStepIndex ? (
                      <i className="fas fa-check"></i>
                    ) : (
                      <span>{index + 1}</span>
                    )}
                  </div>
                  <div className="wizard-step-label">{step.label}</div>
                  {index < steps.length - 1 && <div className="wizard-step-line"></div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className="wizard-content">
        {renderStep()}
      </div><br/>
    </div>
  );
};

export default AddMpxTenantWizardPage;
